/**
 * Score de confiance d'un groupe de détection — heuristique simple et
 * explicable, 0..1. Il qualifie la DÉTECTION (le regroupement est-il
 * plausible ?), pas la gravité du désordre.
 *
 * Les poids sont volontairement grossiers : ils servent à ordonner les cartes
 * et à colorer un badge, pas à filtrer automatiquement. À recalibrer sur de
 * vrais plans terrain en même temps que les seuils de `grouping.ts`.
 */

import { TEMPORAL_GAP_MS } from "./grouping-constants";

/**
 * En dessous de ce ratio de la diagonale du plan, une entité SEULE est
 * considérée comme minuscule (micro-trait probablement accidentel).
 */
export const TINY_ENTITY_RATIO = 0.002;

/**
 * Nombre de photos au-delà duquel le rattachement devient suspect : une
 * observation porte 1 à 2 photos, 3 rarement, 4+ exceptionnellement (retour
 * terrain) — au-delà, des photos appartiennent sans doute à autre chose.
 */
export const MANY_MARKERS_COUNT = 3;
export const EXCESSIVE_MARKERS_COUNT = 4;

/**
 * Confiance fixe des groupes « vue d'ensemble » : la détection repose sur une
 * convention de forme (ronds vs flèches), solide mais pas infaillible.
 */
export const OVERVIEW_CONFIDENCE = 0.7;

export interface ConfidenceInput {
  /** `defectName` de chaque entité tracée du groupe. */
  tracedDefects: (string | null)[];
  /** Plage `createdAt` des membres horodatés, null si aucun. */
  timeRange: { start: number; end: number } | null;
  /** Au moins un tracé sans `createdAt` (dessin antérieur au palier capture). */
  missingCreatedAt: boolean;
  hasText: boolean;
  /** Nombre de marqueurs photo rattachés. */
  markerCount: number;
  /** Groupe réduit à une seule entité minuscule (cf. TINY_ENTITY_RATIO). */
  singleTinyEntity: boolean;
}

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

/** Calcule le score de confiance d'un groupe. */
export function scoreConfidence(input: ConfidenceInput): number {
  let score = 0.5;

  // Cohérence du désordre : un seul désordre connu = signal fort.
  const defects = new Set(
    input.tracedDefects.filter((d): d is string => d !== null),
  );
  const hasNullDefect = input.tracedDefects.some((d) => d === null);
  if (defects.size === 1 && !hasNullDefect) score += 0.25;
  else if (defects.size >= 1) score += 0.1;
  else score -= 0.2;

  // Cohérence temporelle : tracés posés d'affilée = un même geste terrain.
  if (
    input.timeRange &&
    input.timeRange.end - input.timeRange.start <= TEMPORAL_GAP_MS
  ) {
    score += 0.1;
  }
  if (input.missingCreatedAt) score -= 0.1;

  // Contexte rattaché : un texte ou une photo confirme un vrai relevé —
  // mais un déluge de photos signale un rattachement probablement trop large.
  if (input.hasText) score += 0.1;
  if (input.markerCount >= 1) score += 0.1;
  if (input.markerCount >= EXCESSIVE_MARKERS_COUNT) score -= 0.2;
  else if (input.markerCount >= MANY_MARKERS_COUNT) score -= 0.05;

  if (input.singleTinyEntity) score -= 0.15;

  return Math.round(clamp(score, 0.05, 0.98) * 100) / 100;
}
